'use strict';

module.exports = /*@ngInject*/ function(
    $log,
    $q,
    $window,
    LocalStorageService,
    LsCache
) {
    /**
     * Hash of pending requests.
     * Each key will have a value of
     * an array of deferreds, that will be
     * resolved when a response is received
     * for the pending request.
     * This is global so that the multiple
     * instances of ajaxLocalStorageService
     * have access to it.
     * @type {Object}
     */
    var pendingRequests = $window.pendingRequests = $window.pendingRequests || {};

    var AjaxLocalStorageService = {
        /**
         * Add a pending request for a given cache key
         * @param {String} queueKey Queue name
         * @param {Object} deferred $q deferred to be resolved with pending response
         */
        addPendingRequest: function(queueKey, deferred) {
            if(pendingRequests[queueKey]) {
                pendingRequests[queueKey].push(deferred);
            } else {
                $log.error('~~~ No pending request queue created for ' + queueKey);
            }

            return this;
        },

        /**
         * Creates a pending requests queue for a given cache key
         * @param  {String} queueKey Queue name
         * @return {Object} Returns the LocalStorageService for chaining purposes.
         */
        createPendingRequestQueue: function(queueKey) {
            pendingRequests[queueKey] = [];
            return this;
        },

        /**
         * Flushes the entirely of the LocalStorage cache,
         * and removes any pending requests.
         * @return {Object} Returns the LocalStorageService for chaining purposes.
         */
        flushCache: function() {
            pendingRequests = {};
            LsCache.flush();
            return this;
        },

        /**
         * Generates an cache key for ajax caching.
         * Keys are generated based on url, request method type (http verb),
         * query params and any data.
         * @param  {Object} config A $http request config object
         * @return {String}        Generated cache key for an ajax request.
         */
        getAjaxCacheKey: function(config) {
            var cacheKey;

            // generate key from URL, method, params and data
            cacheKey = config.url + '-' + config.method;

            function appendObjValues(obj) {
                var key;

                if (!obj) { return; }

                for (key in obj) {
                    cacheKey += '-' + key + '=' + obj[key];
                }

                return cacheKey;
            }

            appendObjValues(config.params);
            appendObjValues(config.data);

            return cacheKey;
        },

        /**
         * Get the pending requests for a given cache key,
         * and delete cache key from the pendingRequests hash.
         * @param  {String} queueKey Queue name
         * @return {Array}          Returns an array of pending deferreds or an empty array if nothing is pending
         */
        getPendingRequestQueue: function(queueKey) {
            var reqs = pendingRequests[queueKey];
            delete pendingRequests[queueKey];
            return reqs || [];
        },

        /**
         * Checks to see if an ajax request is cached. If the request is cached,
         * the given deferred will immediately be resolved with the cache data.
         * If the request is not cached, the service will check to see if the request
         * is pending. If it is pending, the request will be added to a queue so that
         * no duplicate requests are sent out. The queue will be resolved once a response
         * is received. If the original request is not pending, the service will create
         * a new pending queue to track the requests progress.
         * @param  {Object}  req        $http request object
         * @param  {Object}  deferred   $q deferred to resolve with cached data
         * @param  {Object}  [options]  Optional LsCache options
         *                              Options takes the following optional attributes:
         *                              {
         *                                    cacheKey: '',     // localStorage cache key
         *                                    compress: true,   // should the cached response be compressed?
         *                                    expiryTime: 1000  // localStorage cache time in milliseconds
         *                              }
         * @return {Boolean}          Returns true is the request is cached; false otherwise.
         */
        isAjaxCached: function(req, deferred, options) {
            var self = this;
            var cacheKey;
            var value;

            options = options || {};

            cacheKey = options.cacheKey || this.getAjaxCacheKey(req);
            value = this.getCache(cacheKey, options);

            // has a cache value?
            // then return that and be done with it
            if (value) {
                $log.debug('~~~ Cache found for ' + cacheKey + '. Resolving deferred.', value);
                deferred.resolve(value);
                return true;

            // no cache value but a request is pending?
            // queue up deferred to be resolved once
            // a response is received
            } else if (this.isPending(cacheKey)) {
                $log.debug('~~~ Deferred pending for ' + cacheKey);
                this.addPendingRequest(cacheKey, deferred);
                return true;

            // no cache value and no pending request
            // spin off a new request and create pending
            // request in case additional requests come in
            } else {
                this.createPendingRequestQueue(cacheKey);

                deferred.promise
                    .then(function onPendingRes(res) {
                        $log.debug('~~~ Deferred received for ' + cacheKey + '. Resolving all pending deferreds and caching.', res);
                        self.cache(cacheKey, res, options);
                        self.resolvePendingReqs(cacheKey, res);
                        return res;
                    })
                    .catch(function onPendingErr(err) {
                        $log.error('~~~ Deferred rejected for ' + cacheKey + '. Rejected all pending deferreds.', err);
                        self.rejectPendingReqs(cacheKey, err);
                        return $q.reject(err);
                    });

                return false;
            }
        },

        /**
         * Checks whether a given cacheKey is pending.
         * @param  {String}  cacheKey The cache key to check to see its pending state.
         * @return {Boolean}          True if request is pending; false otherwise.
         */
        isPending: function(cacheKey) {
            return cacheKey in pendingRequests;
        },

        /**
         * Rejects all pending request for a given cache key with the given error.
         * Any cache for the given key is also immediately invalidated.
         * @param  {String} cacheKey The pending cache key to reject.
         * @param  {Object} err      Error to reject pending requests with.
         * @return {Object}          Returns the LocalStorageService for chaining purposes.
         */
        rejectPendingReqs: function(cacheKey, err) {
            var reqs = this.getPendingRequestQueue(cacheKey);

            while (reqs.length) {
                reqs.shift().reject(err);
            }

            return this.removeCache(cacheKey);
        },

        /**
         * Removes a given cache key from the cache
         * and removes any associated pending requests
         * @param  {String} cacheKey The cache key to remove from LocalStorage.
         * @return {Object}          Returns the LocalStorageService for chaining purposes.
         */
        removeCache: function(cacheKey) {
            this.getPendingRequestQueue(cacheKey);
            LsCache.remove(cacheKey);
            return this;
        },

        /**
         * Resolves all pending requests for a given cache key.
         * @param  {String} cacheKey The pending cache key to resolve.
         * @param  {Object} res      Response to resolve pending requests with.
         * @return {Object}          Returns the LocalStorageService for chaining purposes.
         */
        resolvePendingReqs: function(cacheKey, res) {
            var reqs = this.getPendingRequestQueue(cacheKey);

            while (reqs.length) {
                reqs.shift().resolve(res);
            }

            return this;
        }
    };

    return angular.extend({}, LocalStorageService, AjaxLocalStorageService);
};
